// Client-side validation for the admin agent-release upload. The release
// artifact (`release.sh bundle`, ADR 0010 §9.1) is a deterministic tar of
// `bin/tradval-agent` + `manifest.json` — not a bare Mach-O. The backend
// re-verifies the sha256 + version and re-parses the tar
// (parent/admin.py::_extract_bundle_manifest), so this is defense-in-depth +
// UX: read the version from the bundle's manifest.json and compute the
// whole-archive sha256 the operator would otherwise hand-type, and confirm the
// inner binary is a Mach-O before upload.

// Matches the backend release PK charset (parent/admin.py VERSION_PATTERN).
const VERSION_PATTERN = /^[A-Za-z0-9._-]+$/

// Mach-O magic numbers (every flavor + byte-swapped variant). The signed
// tradval-agent inner binary is always one of these (a universal build starts
// with the fat magic 0xcafebabe); rejecting anything else stops an operator
// shipping a corrupt or wrong-format inner binary to real Macs.
const MACH_O_MAGICS: Record<number, true> = {
  0xfeedface: true, // 32-bit
  0xfeedfacf: true, // 64-bit
  0xcafebabe: true, // fat / universal
  0xcffaedfe: true, // 64-bit, byte-swapped
  0xcefaedfe: true, // 32-bit, byte-swapped
  0xbebafeca: true, // fat, byte-swapped
}

function isMachO(bytes: Uint8Array): boolean {
  if (bytes.length < 4) {
    return false
  }
  const bigEndian = ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0
  const littleEndian = ((bytes[3] << 24) | (bytes[2] << 16) | (bytes[1] << 8) | bytes[0]) >>> 0
  return Object.hasOwn(MACH_O_MAGICS, bigEndian) || Object.hasOwn(MACH_O_MAGICS, littleEndian)
}

export async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

interface TarEntry {
  name: string
  content: Uint8Array
}

function readCString(bytes: Uint8Array, off: number, len: number): string {
  let s = ''
  for (let i = off; i < off + len; i++) {
    const c = bytes[i]
    if (c === 0) {
      break
    }
    s += String.fromCharCode(c)
  }
  return s
}

function readOctal(bytes: Uint8Array, off: number, len: number): number {
  let s = ''
  for (let i = off; i < off + len; i++) {
    const c = bytes[i]
    if (c === 0x20 || c === 0) {
      if (s) {
        break
      }
      continue
    }
    s += String.fromCharCode(c)
  }
  return s ? parseInt(s, 8) : 0
}

// Minimal tar reader for the update bundle. Collects regular-file entries
// (typeflag '0' / NUL) by their ustar name; skips directories ('5') and pax
// extended headers ('x' / 'g') — bsdtar's default "restricted pax" emits one
// per entry carrying only mtime/xattrs, never a path override for the bundle's
// short names, so the ustar name field is authoritative.
function readTarEntries(bytes: Uint8Array): TarEntry[] {
  const entries: TarEntry[] = []
  let off = 0
  while (off + 512 <= bytes.length) {
    const header = bytes.subarray(off, off + 512)
    if (header.every((b) => b === 0)) {
      break
    }
    const name = readCString(bytes, off, 100)
    const size = readOctal(bytes, off + 124, 12)
    if (!Number.isFinite(size) || size < 0) {
      break
    }
    const typeflag = bytes[off + 156]
    off += 512
    if (typeflag === 0x30 || typeflag === 0) {
      entries.push({ name, content: bytes.subarray(off, off + size) })
    }
    off += Math.ceil(size / 512) * 512
  }
  return entries
}

export type AgentBundleValidation =
  | { ok: true; version: string; sha256: string }
  | { ok: false; message: string }

export async function validateAgentBundle(file: File): Promise<AgentBundleValidation> {
  if (/bootstrap/i.test(file.name)) {
    return {
      ok: false,
      message:
        'This looks like the bootstrap. It ships inside Agent.app via the installer, not through this page (ADR 0002).',
    }
  }

  const buf = await file.arrayBuffer()
  const entries = readTarEntries(new Uint8Array(buf))
  if (entries.length === 0) {
    return {
      ok: false,
      message: 'Not a tradval-agent update bundle. Expected the tar from `release.sh bundle`.',
    }
  }

  const manifest = entries.find((e) => e.name === 'manifest.json' || e.name.endsWith('/manifest.json'))
  if (!manifest) {
    return {
      ok: false,
      message: 'Bundle is missing manifest.json. Rebuild it with `release.sh bundle`.',
    }
  }

  let version: unknown
  try {
    const parsed: unknown = JSON.parse(new TextDecoder().decode(manifest.content))
    version =
      typeof parsed === 'object' && parsed !== null
        ? (parsed as Record<string, unknown>).bundle_version
        : undefined
  } catch {
    return { ok: false, message: 'Bundle manifest.json is not valid JSON.' }
  }
  if (typeof version !== 'string' || !VERSION_PATTERN.test(version)) {
    return { ok: false, message: 'Bundle manifest.json has no valid bundle_version.' }
  }

  const inner = entries.find((e) => e.name === 'tradval-agent' || e.name.endsWith('/tradval-agent'))
  if (!inner) {
    return { ok: false, message: 'Bundle is missing bin/tradval-agent.' }
  }
  if (!isMachO(inner.content)) {
    return {
      ok: false,
      message: 'Bundle bin/tradval-agent is not a Mach-O binary. Expected a signed tradval-agent Mach-O.',
    }
  }

  return { ok: true, version, sha256: await sha256Hex(buf) }
}
